import React, { createContext, useContext, useEffect, useState, ReactNode, useMemo, useCallback } from 'react';
import { useAuth as useClerkAuth, useUser } from '@clerk/clerk-react';
import { supabase } from '@/integrations/supabase/client';

export interface AuthContextType {
  userId: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  clerkUserId: string | null;
  user: any;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { isSignedIn, isLoaded, signOut: clerkSignOut, userId: clerkUserId, getToken } = useClerkAuth();
  const { user } = useUser();
  const [userId, setUserId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Memoize stable user data to prevent unnecessary re-renders
  const stableUserData = useMemo(() => {
    if (!user) return null;
    return {
      email: user.primaryEmailAddress?.emailAddress || '',
      fullName: user.fullName || '',
      firstName: user.firstName || '',
      username: user.username || '',
      imageUrl: user.imageUrl || ''
    };
  }, [
    user?.primaryEmailAddress?.emailAddress,
    user?.fullName,
    user?.firstName,
    user?.username,
    user?.imageUrl
  ]);

  // Set up Supabase session with Clerk JWT when user is authenticated
  useEffect(() => {
    const setupSupabaseSession = async () => {
      if (isSignedIn && clerkUserId && getToken) {
        try {
          // Get the JWT token from Clerk
          const token = await getToken({ template: 'supabase' });
          
          if (token) {
            // Set the session in Supabase with the Clerk JWT
            const { error } = await supabase.auth.setSession({
              access_token: token,
              refresh_token: 'dummy-refresh-token' // Clerk handles refresh
            });
            
            if (error) {
              console.warn('[AuthContext] Error setting Supabase session:', error.message);
            } else {
              console.log('[AuthContext] Successfully set Supabase session with Clerk JWT');
            }
          }
        } catch (error) {
          console.warn('[AuthContext] Error getting Clerk token for Supabase:', error);
        }
      } else if (!isSignedIn) {
        // Clear Supabase session when user signs out
        await supabase.auth.signOut();
      }
    };

    if (isLoaded) {
      setupSupabaseSession();
    }
  }, [isSignedIn, clerkUserId, isLoaded, getToken]);

  // Map Clerk user to Supabase user and get/create user record
  useEffect(() => {
    const mapUserToSupabase = async () => {
      if (!isLoaded) return;
      
      // Skip if we already have a userId to prevent re-mapping
      if (userId) return;
      
      // Only set loading to true if we're actually going to do work
      const shouldMap = isSignedIn && clerkUserId && stableUserData;
      if (!shouldMap && !isLoading) {
        // User not signed in, no need to keep loading
        setUserId(null);
        return;
      }
      
      if (!shouldMap) {
        setIsLoading(false);
        setUserId(null);
        return;
      }
      
      try {
        console.log('[AuthContext] User signed in, mapping to Supabase:', clerkUserId);
        
        // First check if user mapping exists - using maybeSingle() to prevent 406 errors
        const { data: existingMapping, error: mappingLookupError } = await supabase
          .from('user_mapping')
          .select('supabase_id')
          .eq('clerk_id', clerkUserId)
          .maybeSingle();

        let supabaseUserId: string | null = null;

        if (existingMapping) {
          // User mapping exists
          supabaseUserId = existingMapping.supabase_id;
          console.log('[AuthContext] Found existing mapping:', supabaseUserId);
        } else if (mappingLookupError) {
          // Log any actual errors (but not "no rows" which is expected for new users)
          console.warn('[AuthContext] Error looking up user mapping:', mappingLookupError);
        }

        // If no existing mapping, try to create a new user and mapping
        if (!supabaseUserId && stableUserData) {
          console.log('[AuthContext] Creating new user mapping for:', clerkUserId);
          
          try {
            // First create user record
            const { data: newUser, error: userError } = await supabase
              .from('users')
              .insert({
                email: stableUserData.email,
                full_name: stableUserData.fullName,
                avatar_url: stableUserData.imageUrl,
                display_name: stableUserData.firstName || stableUserData.username
              })
              .select('user_id')
              .maybeSingle();

            if (userError) {
              console.error('[AuthContext] Error creating user:', userError);
              // If user already exists (e.g., email conflict), try to find the existing user
              if (userError.code === '23505' && stableUserData.email) {
                console.log('[AuthContext] User might already exist, looking up by email');
                const { data: existingUser } = await supabase
                  .from('users')
                  .select('user_id')
                  .eq('email', stableUserData.email)
                  .maybeSingle();
                  
                if (existingUser) {
                  supabaseUserId = existingUser.user_id;
                  console.log('[AuthContext] Found existing user by email:', supabaseUserId);
                } else {
                  throw userError; // Re-throw if we can't find the user by email
                }
              } else {
                throw userError; // Re-throw other errors
              }
            } else if (newUser) {
              supabaseUserId = newUser.user_id;
              console.log('[AuthContext] Created new user:', supabaseUserId);
            }

            // Create user mapping if we have a valid user ID
            if (supabaseUserId) {
              const { error: mappingError } = await supabase
                .from('user_mapping')
                .insert({
                  clerk_id: clerkUserId,
                  supabase_id: supabaseUserId
                });

              if (mappingError) {
                console.error('[AuthContext] Error creating user mapping:', mappingError);
                // Continue anyway as we have a valid user ID
              } else {
                console.log('[AuthContext] Created new user mapping');
              }
            }
          } catch (error) {
            console.error('[AuthContext] Error in user creation/mapping:', error);
            // Don't throw here to allow the user to continue with limited functionality
            // The app can still work without Supabase user ID in some cases
          }
        }

        setUserId(supabaseUserId);
      } catch (error) {
        console.error('[AuthContext] Error in user mapping:', error);
        setUserId(null);
      } finally {
        setIsLoading(false);
      }
    };

    mapUserToSupabase();
  }, [isSignedIn, isLoaded, clerkUserId, stableUserData]); // Now using stable user data

  // Memoize signOut to prevent unnecessary re-renders
  const signOut = useCallback(async () => {
    await clerkSignOut();
    setUserId(null);
  }, [clerkSignOut]);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo<AuthContextType>(() => ({
    userId,
    isAuthenticated: isSignedIn && userId !== null,
    isLoading: isLoading || !isLoaded,
    clerkUserId,
    user,
    signOut
  }), [userId, isSignedIn, isLoading, isLoaded, clerkUserId, user, signOut]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
